/// <amd-module name="Widgets/SprintBurndownWidget/SprintBurndownWidget" />
import { TeamSettingsIteration } from "Dashboards/Clients/contracts/Work";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { Lightbox } from "Reporting/Lightbox/Lightbox";
import { format, localeFormat } from "VSS/Core/Util/String";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { KeyCode } from "VSSUI/Util";
import { getSetIterationsMessage } from "WidgetCommon/IterationDatesMessage/SetIterationDatesMessage";
import AnalyticsSprintBurndownViewComponent from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownViewComponent";
import * as Resources from "Widgets/SprintBurndownWidget/Resources";
import { SprintBurndownWidgetDataProvider } from "Widgets/SprintBurndownWidget/SprintBurndownWidgetDataProvider";
import { SprintBurndownSettings } from "./SprintBurndownSettings";
import { isLegacyWidget, SprintBurndownSettingsManager } from "./SprintBurndownSettingsManager";

interface ISprintBurndownWidgetState {
    isLegacy: boolean;
    widgetSettings: SprintBurndownSettings;
    currentIteration: TeamSettingsIteration;
    iterationNeedsConfiguration: boolean;
}

interface IDetailChartImageProps {
    height: number;
    width: number;
    src: string;
    alt: string;
    onClick: () => void;
    onKeyPress: (event: React.KeyboardEvent) => void;
    tabIndex: number;
    role: string;
    className: string;
}

export class SprintBurndownWidget extends VssComponent<WidgetProps, ISprintBurndownWidgetState> {
    private static reservedNonChartHeight = 75;
    private static reservedNonChartWidth = 30;
    public static componentType = "sprintBurndownWidget";
    private widgetDataProvider: SprintBurndownWidgetDataProvider;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props);

        this.widgetDataProvider = new SprintBurndownWidgetDataProvider(context.pageContext);
    }

    public async componentDidMount() {
        this.reload();
    }

    public async componentDidUpdate(prevProps: Readonly<WidgetProps>): Promise<void> {
        if (JSON.stringify(this.props) != JSON.stringify(prevProps)) {
            this.reload();
        }
    }

    public render(): JSX.Element | null {
        return this.renderWidgetOrDefault();
    }

    private async reload() {
        try {
            const isLegacy = isLegacyWidget(this.props);
            if (!isLegacy) {
                this.setState({ isLegacy: isLegacy });
                return; // Return without notification - Allow Modern widget codepath to render and communicate directly, bypassing the legacy widget.
            }

            try {
                if (this.props.teamContext == null || this.props.teamContext.id == null) {
                    // Precondition is violated.
                    // There's no clear action to  ask the user to do here.  We need to get an RCA if this path is reached.
                    // This path is handled as an exception by the Widget Host, the widget isn't responsible for this case.
                    throw new Error("Team context could not be found for Sprint Burndown Widget.");
                }

                // Load or generate default settings
                const settings = await new SprintBurndownSettingsManager(this.context.pageContext, this.props.teamContext).ensureInitialSettings(
                    this.props
                );

                const currentIterations = await this.widgetDataProvider.getSprintMetadata(settings.team.teamId);
                if (!currentIterations || currentIterations.length === 0) {
                    throw new Error("Unable to fetch widget data");
                }
                const currentIteration = currentIterations[0];
                this.setState({
                    isLegacy: isLegacy,
                    currentIteration: currentIteration,
                    widgetSettings: settings,
                    iterationNeedsConfiguration:
                        currentIteration.attributes == null || !currentIteration.attributes.startDate || !currentIteration.attributes.finishDate
                });
            } catch (error) {
                if (
                    // ask the user to reconfigure their sprints in a couple of well defined scenarios
                    (error != null && (error.message as string).indexOf("does not exist") >= 0) || // If the WIT API does not ack query for the team's sprint metadata,
                    (error.message != null && (error.message as string).indexOf("No current iteration was found") >= 0) // If the WIT API could not locate an appropriate iteration to render
                ) {
                    this.setState({
                        isLegacy: isLegacy,
                        iterationNeedsConfiguration: true
                    });
                } else {
                    throw error;
                }
            }
            this.props.onWidgetLoaded();
            WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props, { isLegacy: isLegacy });
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
        }
    }

    private renderWidgetOrDefault(): JSX.Element | null {
        if (this.state == null) {
            // Loading - Not ready to render yet.
            return null;
        }

        if (!this.state.isLegacy) {
            return (
                <AnalyticsSprintBurndownViewComponent
                    {...this.props}
                    suppressAnimations={!this.props.showLoadAnimations}
                    onRenderCompletion={this.props.onWidgetLoaded}
                />
            );
        }

        if (this.state.iterationNeedsConfiguration) {
            return getSetIterationsMessage(this.context.pageContext, Resources.GenericTitle, this.props.size);
        } else {
            return (
                <>
                    <div className="sprint-burndown">
                        {this.renderWidgetTitle()}
                        <div>{this.renderChartImage()}</div>
                    </div>
                </>
            );
        }
    }

    private renderWidgetTitle(): JSX.Element | null {
        if (!this.state || this.state.iterationNeedsConfiguration) {
            return null;
        }

        if (!this.state.currentIteration) {
            return null;
        }

        const currentIteration = this.state.currentIteration;
        const localizedStartDate = this.localizeToMonthDay(currentIteration.attributes.startDate);
        const localizedFinishDate = this.localizeToMonthDay(currentIteration.attributes.finishDate);
        return (
            <h2 className="title">
                {this.state.currentIteration.name}
                <div className="subtitle">{localeFormat(Resources.IterationDateRange, localizedStartDate, localizedFinishDate)}</div>
            </h2>
        );
    }

    private localizeToMonthDay(date: Date) {
        return date.toLocaleString(undefined, { month: "long", day: "numeric", timeZone: "UTC" });
    }

    private renderChartImage(): JSX.Element {
        let className = "small-chart-container";
        const isHidden = !this.state || this.state.iterationNeedsConfiguration;
        const width = Math.floor(this.props.size.width - SprintBurndownWidget.reservedNonChartWidth);
        const height = Math.floor(this.props.size.height - SprintBurndownWidget.reservedNonChartHeight);
        // Determines if we include labelling, for enhanced presentation in larger widget/lightbox.
        const showDetails = width >= 400 && height >= 300;
        let imgProps: Partial<IDetailChartImageProps> = {};
        if (!isHidden) {
            const teamId = this.state.widgetSettings.team.teamId;
            const chartImageUrl = this.widgetDataProvider.getChartImageUrl(width, height, showDetails, teamId, this.state.currentIteration);
            imgProps = {
                height: height,
                width: width,
                src: chartImageUrl,
                alt: format(Resources.Title, this.state.currentIteration.name),

                className: "sprint-burndown-chart"
            } as IDetailChartImageProps;

            const widgetHasLightbox = isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards-web.sprint-burndown-availability", false);

            if (!this.props.avoidShowingModalUI && !widgetHasLightbox) {
                imgProps.onClick = this.renderDetailChart;
                imgProps.onKeyPress = this.keyPressHandler;
                imgProps.tabIndex = 0;
                imgProps.role = "button";
                imgProps.className = "sprint-burndown-chart clickable";
            }
        } else {
            className += "--hidden";
        }

        return (
            <div className={className}>
                <img {...imgProps} />
            </div>
        );
    }

    private keyPressHandler = (event: React.KeyboardEvent) => {
        if (event.charCode === KeyCode.enter || event.charCode === KeyCode.space) {
            this.renderDetailChart();
        }
    };

    // Note: This proprietary lightbox is an obsolete pattern. Cheese move to normalize this is a minor pending issue, given the new widget experience uses lightbox.
    private renderDetailChart = () => {
        const width = Math.floor(window.innerWidth * 0.65);
        const height = Math.floor(window.innerHeight * 0.65);
        const teamId = this.state.widgetSettings.team.teamId;
        const title = format(Resources.Title, this.state.currentIteration.name);
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <Lightbox onDismiss={onDismiss} className="sprint-burndown-detail-chart-lightbox">
                    <div className="detail-chart">
                        <h2>{title}</h2>
                        <img
                            src={this.widgetDataProvider.getChartImageUrl(width, height, true /* showDetails */, teamId, this.state.currentIteration)}
                            className="sprint-burndown-detail-chart-image"
                        />
                    </div>
                </Lightbox>
            );
        });
    };
}
VssComponent.register(SprintBurndownWidget.componentType, SprintBurndownWidget);
